/* 
    SubChain Edit - add, edit, delete subchains, as well as excel import and export

    TODO: manually enter subchain by clicking on map

*/


import React, { useState } from 'react'
//components
import { Button } from '@components/ui/buttons/Button'
import EditableTable from '@components/ui/editableTable/EditableTable'
import { ValidationErrors } from '@components/ui/messages/ValidationErrors'
import { AddRowDialog } from '../shared/AddRowDialog'
import { POIWarning } from '../shared/POIWarning'
//table config
import { SubchainColumnNameMap } from "../tableConfig/jobControlColumnNameMaps"
import { SubchainSchema } from '../tableConfig/jobControlSchemas'
//hooks
import { useImportExcel } from '@hooks/useImportExcel'
import useConfirmDialog from '@hooks/useConfirmDialog'
import { useExportExcel } from "@hooks/useExportExcel"
import { useSubchainActions } from './hooks/useSubchainActions'
import Checkbox from '@components/ui/checkbox/Checkbox'
import Label from '@components/ui/labels/Label'

//when exporting subchains to excel, swap out US/DS Site AGMIDs for Site_Name strings
const formatSubchainsForExport = (data) => {
    return data.map(subchain => {
        return {
            ...subchain,
            US_Site: subchain.UpstreamSite.Site_Name,
            DS_Site: subchain.DownstreamSite.Site_Name,
        }
    })
}

/**
 * 
 * @param {DropdownOption} selectedJob
 * 
 * @returns {JSX.Element}
 * 
 * @category Components - Job Control
 */
export const SubChainEdit = ({selectedJob}) => {
    const {FileUploadButton, fileName, setFileName, ImportColumnsMissingList, setMissingColumns} = useImportExcel()
    const [ConfirmDialog, requestConfirm] = useConfirmDialog()
    const {handleExportToExcel} = useExportExcel()
    const {
        handleSubmit,
        data,
        setData,
        handleDeleteAllSubchains,
        handleDeleteOneSubchain,
        handleDownloadSCTemplate,
        validationErrors,
        agmOptions,
        agmRawData,
        addSubchainRow,
        isUnlockedWithPOIs
    } = useSubchainActions(selectedJob, requestConfirm);

    const [reorderChecked, setReorderChecked] = useState(false);

    const [showAddRow, setShowAddRow] = useState(false)

    const onReorderCheckChange = (value) => {
        const {checked} = value;
        setReorderChecked(checked);
    }

    const handleAddRowClick = async() => {
        //let them know that they may have a better experience if they use excel import instead, let them choose what to do

        const confirmationMessage = (
            <div className='text-left items-center'>
                <p className='mb-4'>You can add a single row anywhere in the table using this option.</p>

                <p className='mb-4'>If you need to add multiple rows, or want more control over your data, we recommend using Excel Import instead.
                </p>

                <p className='mb-1'>To update Subchains using Excel Import:</p>
                <ul className='mb-2'>
                    <li>1. <strong>Delete</strong> existing Subchains</li>
                    <li>2. Click <strong>Import</strong> and upload your Excel file</li>
                    <li>3. Click <strong>Submit</strong> to apply the changes</li>
                </ul>
            </div>
        );

        const isConfirmed = await requestConfirm(confirmationMessage, "Continue to Add Row", "Cancel", "primary")

        if(isConfirmed){
            setShowAddRow(true)
        }
    }

    return (
        <div className='martrak-max-width'>

            {/* template download - always visible */}
            <div className='flex justify-end'>
                <Button
                    variant='noBackground'
                    onClick={handleDownloadSCTemplate}
                    className='text-sm'
                >
                    <i className="fa-solid fa-circle-arrow-down text-purple-800 mr-2"></i>
                    Download Template
                </Button>
            </div>

            {selectedJob ?
            (
                <>
                {selectedJob.AGMCount > 0 ? (
                    <>
                    {/* excel related buttons */}
                    <div className='flex justify-end'>

                        <Button 
                            variant='noBackground' 
                            onClick={() => handleExportToExcel(formatSubchainsForExport(data), SubchainColumnNameMap, "subchains", selectedJob.label)}
                            className='text-sm'
                        >
                            <i className="fa-solid fa-file-export text-purple-800 mr-2"></i>
                            Export
                        </Button>

                        <FileUploadButton 
                            setData={setData}
                            data={data}
                            route="subchains"
                            columnMap={SubchainColumnNameMap}
                            schema={SubchainSchema}
                            agmOptions={agmOptions} //sending to change string names to AGMID -  Bryan
                            disabled={selectedJob.IsLocked}
                            JobID={selectedJob.value}
                        /> 
                    </div>

                    {/* import error messages */}
                    <ImportColumnsMissingList />

                    {/* imported file name, if any */}
                    {fileName ? <p className='my-4 text-center italic'>{fileName}</p> : null}

                    {isUnlockedWithPOIs && <POIWarning />}
                    
                    <div className='flex gap-5 border justify-end'>
                        <Label>Auto Re-Order Order Number</Label>
                        <Checkbox 
                            name='reorder-toggle'
                            initiallyChecked={false}
                            onChange={onReorderCheckChange}
                        />
                    </div>
                    

                    {/* table */}
                    {(data.length > 0 && agmRawData.length > 0) ?
                        
                        <EditableTable 
                            data={data} 
                            setData={setData} 
                            schema={SubchainSchema} 
                            canDeleteRow={true} 
                            handleDeleteRow={handleDeleteOneSubchain}
                            validationErrors={validationErrors}
                            agmOptions={agmOptions}
                            agmRawData={agmRawData}
                            subchains={data}
                            reorderChecked={reorderChecked}
                            jobIsLocked={selectedJob.IsLocked}
                            isPaginated={true}
                        />

                        :

                        <h1>Import Subchains or Add Row</h1>
                    }

                    
                    {showAddRow &&
                        <AddRowDialog
                            dropdownOptions={data.map((SC, i) => {return {value: SC.SCID, label: SC.SC_Name, index: i}})}
                            showDialog={setShowAddRow}
                            onSubmit={(insertIndex, newData) => addSubchainRow(SubchainSchema, setData, insertIndex, newData)}
                            requiredFields={['SC_Name', 'lat', 'lng']}
                            columnNameMap={SubchainColumnNameMap}
                        />
                    }
                    

                    {/* submit and add row buttons */}
                    <div className='flex justify-end mb-5'>
                        <Button 
                            variant="success" 
                            onClick={() => handleAddRowClick()} 
                            className='w-max'
                            disabled={selectedJob.IsLocked}
                        >
                            <i className="fa-solid fa-circle-plus mr-2"></i>
                            Add Row
                        </Button>
                    </div>

                    <div className='flex'>
                        <Button 
                            variant="success" 
                            onClick={() => handleSubmit(setMissingColumns, setFileName)} 
                            className='w-max mr-2'
                        >
                            Submit
                        </Button>
                        <Button 
                            variant="danger" 
                            onClick={() => handleDeleteAllSubchains(setMissingColumns, setFileName)} 
                            className='w-max'
                            disabled={selectedJob.IsLocked}
                        >
                            <i className="fa-solid fa-trash-can mr-2"></i>
                            Delete All Subchains
                        </Button>
                    </div>

                    {/* validation error messages */}
                    <ValidationErrors validationErrors={validationErrors} columnNameMap={SubchainColumnNameMap}/>

                </>
                ) : 
                (
                    <div className='flex justify-center mt-10'>
                        <p className='text-purple-600 font-bold italic'>Job must have AGMs before Subchains can be added</p>
                    </div>
                )}
                </>
            ) 
            : 
            (                
                <div className='flex justify-center mt-10'>
                    <p className='text-purple-600 font-bold italic'>Please select a job.</p>
                </div>
            )
            }

            {/* dialog box opens when "Delete All Subchains" button is clicked */}
            <ConfirmDialog />

        </div>
        
    )
}